import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { ArrowLeft, GitBranch, Plus, Edit, Trash2, AlertCircle, Loader2, Info } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useAuth,
} from '@aaas/shared';
import { usePipelineQuery } from '../../hooks/usePipelineQueries';
import pipelineService from '../../services/pipeline.service';
import { RoundList } from '../../components/RoundList';
import { RoundFormDialog } from '../../components/RoundFormDialog';
import { PipelineFormDialog } from '../../components/PipelineFormDialog';
import type { PipelineRound } from '../../types';

export function PipelineDetailPage() {
  const { t } = useTranslation('ats');
  const { pipelineId } = useParams<{ pipelineId: string }>();
  const navigate = useNavigate();
  const { user, currentWorkspace, idToken } = useAuth();
  const queryClient = useQueryClient();

  // Pipeline data
  const {
    data: pipeline,
    isLoading: loading,
    error,
    refetch,
  } = usePipelineQuery({
    pipelineId,
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    enabled: !!user && !!currentWorkspace && !!pipelineId,
  });

  // Pipeline edit dialog state
  const [showPipelineEditDialog, setShowPipelineEditDialog] = useState(false);

  // Pipeline delete dialog state
  const [showPipelineDeleteDialog, setShowPipelineDeleteDialog] = useState(false);
  const [isDeletingPipeline, setIsDeletingPipeline] = useState(false);
  const [pipelineDeleteError, setPipelineDeleteError] = useState<string | null>(null);

  // Round form dialog state
  const [showRoundFormDialog, setShowRoundFormDialog] = useState(false);
  const [roundFormMode, setRoundFormMode] = useState<'create' | 'edit'>('create');
  const [editingRound, setEditingRound] = useState<PipelineRound | null>(null);

  // Round delete dialog state
  const [showRoundDeleteDialog, setShowRoundDeleteDialog] = useState(false);
  const [selectedRound, setSelectedRound] = useState<PipelineRound | null>(null);
  const [isDeletingRound, setIsDeletingRound] = useState(false);
  const [deletingRoundIds, setDeletingRoundIds] = useState<Set<string>>(new Set());
  const [roundDeleteError, setRoundDeleteError] = useState<string | null>(null);

  // Handlers for pipeline actions
  const handleEditPipeline = useCallback(() => {
    setShowPipelineEditDialog(true);
  }, []);

  const handlePipelineEditSuccess = useCallback(() => {
    refetch();
    // Invalidate pipeline-rounds query so CVDetailPage timeline updates
    queryClient.invalidateQueries({ queryKey: ['pipeline-rounds', pipelineId] });
  }, [refetch, queryClient, pipelineId]);

  const handleDeletePipelineClick = useCallback(() => {
    setPipelineDeleteError(null);
    setShowPipelineDeleteDialog(true);
  }, []);

  const handleDeletePipelineConfirm = async () => {
    if (!pipeline || !user || !currentWorkspace) return;

    setIsDeletingPipeline(true);
    setPipelineDeleteError(null);

    try {
      const token = await user.getIdToken();
      await pipelineService.deletePipeline(pipeline.id, currentWorkspace.id, token);
      navigate('/ats/pipelines');
    } catch (err: unknown) {
      let errorMessage = t('pipeline.detail.unknownError');
      if (axios.isAxiosError(err) && err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setPipelineDeleteError(errorMessage);
    } finally {
      setIsDeletingPipeline(false);
    }
  };

  // Helper function to check if round is a default round
  const isDefaultRound = useCallback((roundType: string) => {
    return roundType === 'application_received' || roundType === 'hired';
  }, []);

  // Handlers for round actions
  const handleAddRound = useCallback(() => {
    setRoundFormMode('create');
    setEditingRound(null);
    setShowRoundFormDialog(true);
  }, []);

  const handleEditRound = useCallback((round: PipelineRound) => {
    setRoundFormMode('edit');
    setEditingRound(round);
    setShowRoundFormDialog(true);
  }, []);

  const handleRoundFormClose = useCallback(() => {
    setShowRoundFormDialog(false);
    setEditingRound(null);
  }, []);

  const handleRoundFormSuccess = useCallback(() => {
    refetch();
    // Invalidate pipeline-rounds query so CVDetailPage timeline updates
    queryClient.invalidateQueries({ queryKey: ['pipeline-rounds', pipelineId] });
  }, [refetch, queryClient, pipelineId]);

  const handleDeleteRoundClick = useCallback((round: PipelineRound) => {
    // Prevent deletion of default rounds
    if (isDefaultRound(round.round_type)) {
      return;
    }

    setSelectedRound(round);
    setRoundDeleteError(null);
    setShowRoundDeleteDialog(true);
  }, []);

  const handleDeleteRoundConfirm = async () => {
    if (!selectedRound || !user || !currentWorkspace || !pipelineId) return;

    setIsDeletingRound(true);
    setRoundDeleteError(null);
    setDeletingRoundIds((prev) => new Set(prev).add(selectedRound.id));

    try {
      const token = await user.getIdToken();
      await pipelineService.deleteRound(pipelineId, selectedRound.id, currentWorkspace.id, token);
      await refetch();
      // Invalidate pipeline-rounds query so CVDetailPage timeline updates
      queryClient.invalidateQueries({ queryKey: ['pipeline-rounds', pipelineId] });
      setShowRoundDeleteDialog(false);
      setSelectedRound(null);
    } catch (err: unknown) {
      let errorMessage = t('pipeline.detail.unknownError');
      if (axios.isAxiosError(err) && err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setRoundDeleteError(errorMessage);
    } finally {
      setIsDeletingRound(false);
      setDeletingRoundIds((prev) => {
        const newSet = new Set(prev);
        if (selectedRound) {
          newSet.delete(selectedRound.id);
        }
        return newSet;
      });
    }
  };

  const handleReorderRounds = async (roundIds: string[]) => {
    if (!user || !currentWorkspace || !pipelineId) return;

    const token = await user.getIdToken();
    await pipelineService.reorderRounds(pipelineId, currentWorkspace.id, token, roundIds);
    await refetch();
    // Invalidate pipeline-rounds query so CVDetailPage timeline updates
    queryClient.invalidateQueries({ queryKey: ['pipeline-rounds', pipelineId] });
  };

  // Loading state
  if (loading) {
    return (
      <div className="w-full p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/ats/pipelines')} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          {t('pipeline.detail.back')}
        </Button>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : t('pipeline.detail.loadFailed')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Not found state
  if (!pipeline) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/ats/pipelines')} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          {t('pipeline.detail.back')}
        </Button>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{t('pipeline.detail.notFound')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/ats/pipelines')}
            className="h-10 w-10"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <GitBranch className="h-8 w-8 text-teal-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{pipeline.name}</h1>
              <p className="text-sm text-gray-500">
                {t('pipeline.detail.rounds', { count: pipeline.rounds.length })}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleEditPipeline} className="gap-2">
            <Edit className="h-4 w-4" />
            {t('pipeline.detail.edit')}
          </Button>
          <Button
            variant="outline"
            onClick={handleDeletePipelineClick}
            className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
            {t('pipeline.detail.delete')}
          </Button>
        </div>
      </div>

      {/* Rounds Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900">{t('pipeline.detail.recruitmentRounds')}</h2>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-blue-500 cursor-pointer" />
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <p className="text-sm">
                    <strong>{t('pipeline.detail.defaultRoundsTitle')}</strong>
                  </p>
                  <ul className="text-xs mt-1 space-y-1">
                    <li>• <strong>{t('pipeline.detail.applicationReceived')}</strong> {t('pipeline.detail.applicationReceivedHint')}</li>
                    <li>• <strong>{t('pipeline.detail.hired')}</strong> {t('pipeline.detail.hiredHint')}</li>
                  </ul>
                  <p className="text-xs mt-2 text-gray-500">
                    {t('pipeline.detail.customRoundsHint')}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Button onClick={handleAddRound} className="gap-2">
            <Plus className="h-4 w-4" />
            {t('pipeline.detail.addRound')}
          </Button>
        </div>

        <RoundList
          rounds={pipeline.rounds}
          onEdit={handleEditRound}
          onDelete={handleDeleteRoundClick}
          onReorder={handleReorderRounds}
          deletingIds={deletingRoundIds}
        />
      </div>

      {/* Pipeline Edit Dialog */}
      <PipelineFormDialog
        open={showPipelineEditDialog}
        onClose={() => setShowPipelineEditDialog(false)}
        mode="edit"
        pipeline={pipeline}
        onSuccess={handlePipelineEditSuccess}
      />

      {/* Pipeline Delete Dialog */}
      <AlertDialog
        open={showPipelineDeleteDialog}
        onOpenChange={(open) => {
          if (!isDeletingPipeline) {
            setShowPipelineDeleteDialog(open);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('pipeline.detail.deletePipelineTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('pipeline.detail.deletePipelineConfirm', { name: pipeline.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pipelineDeleteError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <span className="text-sm text-red-600">{pipelineDeleteError}</span>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingPipeline}>{t('config.cancel')}</AlertDialogCancel>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleDeletePipelineConfirm();
              }}
              disabled={isDeletingPipeline}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeletingPipeline ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('config.deleting')}
                </>
              ) : (
                t('pipeline.detail.deletePipelineButton')
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Round Form Dialog */}
      {pipelineId && (
        <RoundFormDialog
          open={showRoundFormDialog}
          onClose={handleRoundFormClose}
          mode={roundFormMode}
          pipelineId={pipelineId}
          round={editingRound}
          onSuccess={handleRoundFormSuccess}
        />
      )}

      {/* Round Delete Dialog */}
      <AlertDialog
        open={showRoundDeleteDialog}
        onOpenChange={(open) => {
          if (!isDeletingRound) {
            setShowRoundDeleteDialog(open);
            if (!open) {
              setSelectedRound(null);
              setRoundDeleteError(null);
            }
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('pipeline.detail.deleteRoundTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('pipeline.detail.deleteRoundConfirm', { name: selectedRound?.name ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {roundDeleteError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <span className="text-sm text-red-600">{roundDeleteError}</span>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingRound}>{t('config.cancel')}</AlertDialogCancel>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleDeleteRoundConfirm();
              }}
              disabled={isDeletingRound}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeletingRound ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('config.deleting')}
                </>
              ) : (
                t('pipeline.detail.deleteRoundButton')
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
